import { v4 as uuidv4 } from 'uuid';

import * as portalApi from '../../../api/portal-api';
import { removeArrayItemAt, replaceArrayItemAt } from '../../../utils/arrayUtil';
import { createErrorToast, createSuccessToast } from '../app';
import { selectCompanyId } from '../company';

import { sortAutomationSteps } from './util';

/* CONSTANTS
========================================================= */
const EMAIL_AUTOMATIONS_SET_CURRENT_AUTOMATION = 'EMAIL_AUTOMATIONS_SET_CURRENT_AUTOMATION';

const EMAIL_AUTOMATIONS_GET_TRIGGERS = 'EMAIL_AUTOMATIONS_GET_TRIGGERS';
const EMAIL_AUTOMATIONS_GET_TRIGGERS_SUCCESS = 'EMAIL_AUTOMATIONS_GET_TRIGGERS_SUCCESS';
const EMAIL_AUTOMATIONS_GET_TRIGGERS_FAIL = 'EMAIL_AUTOMATIONS_GET_TRIGGERS_FAIL';

const EMAIL_AUTOMATIONS_GET_AUTOMATIONS = 'EMAIL_AUTOMATIONS_GET_AUTOMATIONS';
const EMAIL_AUTOMATIONS_GET_AUTOMATIONS_SUCCESS = 'EMAIL_AUTOMATIONS_GET_AUTOMATIONS_SUCCESS';
const EMAIL_AUTOMATIONS_GET_AUTOMATIONS_FAIL = 'EMAIL_AUTOMATIONS_GET_AUTOMATIONS_FAIL';

const EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES = 'EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES';
const EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES_SUCCESS = 'EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES_SUCCESS';
const EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES_FAIL = 'EMAIL_AUTOMATIONS_GET_AUTOMATIONS_FAIL';

const EMAIL_AUTOMATIONS_CREATE_AUTOMATION = 'EMAIL_AUTOMATIONS_CREATE_AUTOMATION';
const EMAIL_AUTOMATIONS_CREATE_AUTOMATION_SUCCESS = 'EMAIL_AUTOMATIONS_CREATE_AUTOMATION_SUCCESS';
const EMAIL_AUTOMATIONS_CREATE_AUTOMATION_FAIL = 'EMAIL_AUTOMATIONS_CREATE_AUTOMATION_FAIL';

const EMAIL_AUTOMATIONS_UPDATE_AUTOMATION = 'EMAIL_AUTOMATIONS_UPDATE_AUTOMATION';
const EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_SUCCESS = 'EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_SUCCESS';
const EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_FAIL = 'EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_FAIL';

const EMAIL_AUTOMATIONS_DELETE_AUTOMATION = 'EMAIL_AUTOMATIONS_DELETE_AUTOMATION';
const EMAIL_AUTOMATIONS_DELETE_AUTOMATION_SUCCESS = 'EMAIL_AUTOMATIONS_DELETE_AUTOMATION_SUCCESS';
const EMAIL_AUTOMATIONS_DELETE_AUTOMATION_FAIL = 'EMAIL_AUTOMATIONS_DELETE_AUTOMATION_FAIL';

const EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP = 'EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP';
const EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP_SUCCESS = 'EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP_SUCCESS';
const EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP_FAIL = 'EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP_FAIL';

const EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP = 'EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP';
const EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP_SUCCESS = 'EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP_SUCCESS';
const EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP_FAIL = 'EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP_FAIL';

const EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP = 'EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP';
const EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP_SUCCESS = 'EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP_SUCCESS';
const EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP_FAIL = 'EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP_FAIL';

/* STATE
========================================================= */
const DEFAULT_STATE = {
  triggers: null,
  automations: null,
  templates: null,
  currentAutomation: null
};

/* ACTIONS
========================================================= */
export const getTriggers = () => async dispatch => {
  dispatch({ type: EMAIL_AUTOMATIONS_GET_TRIGGERS });

  try {
    const triggers = await portalApi.getEmailAutomationTriggers();
    dispatch({ type: EMAIL_AUTOMATIONS_GET_TRIGGERS_SUCCESS, payload: { triggers } });
  } catch (error) {
    dispatch({ type: EMAIL_AUTOMATIONS_GET_TRIGGERS_FAIL, error });
  }
};

export const getAutomations = companyId => async dispatch => {
  dispatch({ type: EMAIL_AUTOMATIONS_GET_AUTOMATIONS });

  try {
    const automations = await portalApi.getEmailAutomations(companyId);

    if (automations.length) {
      dispatch(setCurrentAutomation(automations[0]));
    }

    dispatch({
      type: EMAIL_AUTOMATIONS_GET_AUTOMATIONS_SUCCESS,
      payload: { automations }
    });
  } catch (error) {
    dispatch({ type: EMAIL_AUTOMATIONS_GET_AUTOMATIONS_FAIL, error });
  }
};

export const getAutomationSequences = automationId => async dispatch => {
  dispatch({ type: EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES });

  try {
    const sequences = await portalApi.getEmailAutomationSequences(automationId);
    dispatch({ type: EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES_SUCCESS, payload: { sequences } });
  } catch (error) {
    dispatch({ type: EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES_FAIL, error });
  }
};

export const setCurrentAutomation = newValue => (dispatch, getState) => {
  const currentAutomation = typeof newValue === 'number'
    ? getState().emailAutomations.automations.find(automation => automation.id === newValue)
    : newValue;

  if (selectCurrentAutomation(getState())?.id === currentAutomation?.id) {
    return;
  }

  dispatch(getAutomationSequences(currentAutomation.id));

  dispatch({
    type: EMAIL_AUTOMATIONS_SET_CURRENT_AUTOMATION,
    payload: {
      currentAutomation: {
        ...currentAutomation,
        steps: sortAutomationSteps(currentAutomation.steps)
      }
    }
  });
};

export const createAutomation = (data, trigger) => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());
  const tempId = uuidv4();
  const tempPayload = {
    automation: {
      ...data,
      isEnabled: false,
      id: tempId,
      companyId,
      steps: [],
      trigger
    }
  };

  dispatch({
    type: EMAIL_AUTOMATIONS_CREATE_AUTOMATION,
    payload: tempPayload
  });

  try {
    const automation = await portalApi.createEmailAutomation(companyId, data);
    dispatch({ type: EMAIL_AUTOMATIONS_CREATE_AUTOMATION_SUCCESS, payload: { automation, tempId } });
  } catch (error) {
    dispatch({ type: EMAIL_AUTOMATIONS_CREATE_AUTOMATION_FAIL, tempId });
    dispatch(createErrorToast({ title: 'Something went wrong.', error }));
  }
};

export const updateAutomation = (automationId, updates) => async (dispatch, getState) => {
  const previousAutomationState = selectCurrentAutomation(getState());

  dispatch({ type: EMAIL_AUTOMATIONS_UPDATE_AUTOMATION, payload: { updates } });

  try {
    const automation = await portalApi.updateEmailAutomation(automationId, updates);
    dispatch({ type: EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_SUCCESS, payload: { automation } });
  } catch (error) {
    dispatch({ type: EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_FAIL, payload: { automation: previousAutomationState } });
    dispatch(createErrorToast({ title: 'Something went wrong.' }));
  }
};

export const createAutomationStep = (automationId, data) => async dispatch => {
  const stepId = uuidv4();

  const tempStep = {
    id: stepId,
    emailAutomationId: automationId,
    name: data.name,
    timeframeType: data.timeframe_type,
    timeframeValue: data.timeframe_value,
    isEnabled: true,
    template: {
      id: uuidv4(),
      stepId,
      subject: data.template.subject,
      body: data.template.body,
      signature: data.template.signature
    }
  };

  if (data.notification_settings) {
    tempStep.notification = {
      id: uuidv4(),
      stepId,
      departmentOwner: data.notification_settings?.department_owner,
      projectOwner: data.notification_settings?.project_owner,
      recordWatchers: data.notification_settings?.record_watchers,
      custom: data.notification_settings?.custom
    };
  }

  dispatch({ type: EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP, tempStep });

  dispatch(createSuccessToast({ title: 'Step created successfully' }));

  try {
    const step = await portalApi.createEmailAutomationStep(automationId, data);
    dispatch({ type: EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP_SUCCESS, payload: { step }, tempId: stepId });
  } catch (error) {
    dispatch({ type: EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP_FAIL, tempId: stepId });
    dispatch(createErrorToast({ title: 'Something went wrong.', error }));
  }
};

export const updateAutomationStep = (stepId, data) => async (dispatch, getState) => {
  const previousStepState = selectAutomationStep(stepId)(getState());

  if (data?.notification_settings) {
    data.notification = {
      id: uuidv4(),
      ...data.notification_settings
    };
  }

  const tempStepState = { ...previousStepState, ...data };
  dispatch({ type: EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP, payload: { step: tempStepState } });

  dispatch(createSuccessToast({ title: 'Step updated successfully' }));

  try {
    const step = await portalApi.updateEmailAutomationStep(stepId, data);
    dispatch({ type: EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP_SUCCESS, payload: { step } });
  } catch (error) {
    dispatch({ type: EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP_FAIL, payload: { step: previousStepState } });
    dispatch(createErrorToast({ title: 'Something went wrong.', error }));
  }
};

export const deleteAutomationStep = stepId => async (dispatch, getState) => {
  const step = selectAutomationStep(stepId)(getState());

  dispatch({ type: EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP, payload: { stepId } });

  try {
    await portalApi.deleteEmailAutomationStep(stepId);
    dispatch({ type: EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP_SUCCESS, payload: { stepId } });
  } catch (error) {
    dispatch({ type: EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP_FAIL, payload: { step } });
    dispatch(createErrorToast({ title: 'Something went wrong.' }));
  }
};

export const deleteAutomation = automationId => async (dispatch, getState) => {
  const previousCurrentAutomation = selectCurrentAutomation(getState());
  const previousAutomations = selectCurrentAutomation(getState());
  dispatch({ type: EMAIL_AUTOMATIONS_DELETE_AUTOMATION, payload: { automationId } });

  try {
    await portalApi.deleteEmailAutomation(automationId);
    dispatch({ type: EMAIL_AUTOMATIONS_DELETE_AUTOMATION_SUCCESS, payload: { automationId } });
  } catch (error) {
    dispatch({
      type: EMAIL_AUTOMATIONS_DELETE_AUTOMATION_FAIL,
      payload: { previousCurrentAutomation, previousAutomations }
    });

    dispatch(createErrorToast({ title: 'Something went wrong.' }));
  }
};

/* REDUCER
========================================================= */
const emailAutomations = (state = DEFAULT_STATE, action = null) => {
  switch (action.type) {
    case EMAIL_AUTOMATIONS_GET_AUTOMATIONS:
    case EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES:
    case EMAIL_AUTOMATIONS_GET_AUTOMATIONS_FAIL:
    case EMAIL_AUTOMATIONS_GET_TRIGGERS: {
      return state;
    }

    case EMAIL_AUTOMATIONS_SET_CURRENT_AUTOMATION:
    case EMAIL_AUTOMATIONS_GET_TRIGGERS_SUCCESS:
    case EMAIL_AUTOMATIONS_GET_AUTOMATIONS_SUCCESS: {
      return {
        ...state,
        ...action.payload
      };
    }

    case EMAIL_AUTOMATIONS_GET_AUTOMATION_SEQUENCES_SUCCESS: {
      return {
        ...state,
        currentAutomation: {
          ...state.currentAutomation,
          sequences: action.payload.sequences
        }
      };
    }

    case EMAIL_AUTOMATIONS_CREATE_AUTOMATION: {
      return {
        ...state,
        currentAutomation: action.payload.automation,
        automations: [action.payload.automation, ...(state.automations || [])]
      };
    }

    case EMAIL_AUTOMATIONS_CREATE_AUTOMATION_SUCCESS: {
      return {
        ...state,
        currentAutomation: action.payload.automation,
        automations: replaceArrayItemAt(
          state.automations,
          'id',
          action.payload.tempId,
          action.payload.automation
        )
      };
    }

    case EMAIL_AUTOMATIONS_CREATE_AUTOMATION_FAIL: {
      return {
        ...state,
        currentAutomation: (state.automations && state.automations[0]) || null,
        automations: removeArrayItemAt(state.automations, 'id', action.tempId)
      };
    }

    case EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP:
    {
      return {
        ...state,
        currentAutomation: {
          ...state.currentAutomation,
          steps: sortAutomationSteps([...state.currentAutomation.steps, action.tempStep])
        },
        automations: replaceArrayItemAt(state.automations, 'id', state.currentAutomation.id, {
          ...state.currentAutomation,
          steps: sortAutomationSteps([...state.currentAutomation.steps, action.tempStep])
        })
      };
    }

    case EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP_SUCCESS: {
      const newStepsArray = sortAutomationSteps(replaceArrayItemAt(state.currentAutomation.steps, 'id', action.tempId, action.payload.step));

      return {
        ...state,
        currentAutomation: {
          ...state.currentAutomation,
          steps: newStepsArray
        },
        automations: replaceArrayItemAt(state.automations, 'id', state.currentAutomation.id, {
          ...state.currentAutomation,
          steps: newStepsArray
        })
      };
    }

    case EMAIL_AUTOMATIONS_CREATE_AUTOMATION_STEP_FAIL: {
      const newStepsArray = sortAutomationSteps(removeArrayItemAt(state.currentAutomation.steps, 'id', action.tempId));

      return {
        ...state,
        currentAutomation: {
          ...state.currentAutomation,
          steps: newStepsArray
        },
        automations: replaceArrayItemAt(state.automations, 'id', state.currentAutomation.id, {
          ...state.currentAutomation,
          steps: newStepsArray
        })
      };
    }

    case EMAIL_AUTOMATIONS_UPDATE_AUTOMATION: {
      return {
        ...state,
        currentAutomation: {
          ...state.currentAutomation,
          ...action.payload.updates
        }
      };
    }

    case EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_FAIL:
    case EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_SUCCESS: {
      return {
        ...state,
        currentAutomation: {
          ...state.currentAutomation,
          ...action.payload.automation
        },
        automations: sortAutomationSteps(replaceArrayItemAt(state.automations, 'id', action.payload.automation.id, action.payload.automation))
      };
    }

    case EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP:
    case EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP_FAIL:
    case EMAIL_AUTOMATIONS_UPDATE_AUTOMATION_STEP_SUCCESS: {
      const updatedAutomation = {
        ...state.currentAutomation,
        steps: sortAutomationSteps(replaceArrayItemAt(state.currentAutomation.steps, 'id', action.payload.step.id, action.payload.step))
      };

      return {
        ...state,
        currentAutomation: updatedAutomation,
        automations: sortAutomationSteps(replaceArrayItemAt(state.automations, 'id', state.currentAutomation.id, updatedAutomation))
      };
    }

    case EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP:
    case EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP_SUCCESS: {
      const updatedAutomation = {
        ...state.currentAutomation,
        steps: removeArrayItemAt(state.currentAutomation.steps, 'id', action.payload.stepId)
      };

      return {
        ...state,
        currentAutomation: updatedAutomation,
        automations: replaceArrayItemAt(state.automations, 'id', state.currentAutomation.id, updatedAutomation)
      };
    }

    case EMAIL_AUTOMATIONS_DELETE_AUTOMATION_STEP_FAIL: {
      const updatedAutomation = { steps: sortAutomationSteps([...state.currentAutomation.steps, action.payload.step]) };

      return {
        ...state,
        currentAutomation: updatedAutomation,
        automations: replaceArrayItemAt(state.automations, 'id', state.currentAutomation.id, updatedAutomation)
      };
    }

    case EMAIL_AUTOMATIONS_DELETE_AUTOMATION:
    case EMAIL_AUTOMATIONS_DELETE_AUTOMATION_SUCCESS: {
      const remainingAutomations = removeArrayItemAt(state.automations, 'id', action.payload.automationId);

      return {
        ...state,
        currentAutomation: state.currentAutomation?.id === action.payload.automationId
          ? remainingAutomations[0]
          : state.currentAutomation,
        automations: remainingAutomations
      };
    }

    case EMAIL_AUTOMATIONS_DELETE_AUTOMATION_FAIL: {
      return {
        ...state,
        currentAutomation: action.payload.previousCurrentAutomation,
        automations: action.payload.previousAutomations
      };
    }

    default: {
      if (action.type.startsWith('EMAIL_AUTOMATIONS')) {
        throw new Error(`Unhandled action type: ${action.type}`);
      }

      return state;
    }
  }
};

/* SELECTORS
========================================================= */
export const selectEmailAutomationTriggers = state => state.emailAutomations.triggers;
export const selectEmailAutomations = state => state.emailAutomations.automations;
export const selectCurrentAutomation = state => state.emailAutomations.currentAutomation;
export const selectAutomationStep = stepId => state => state.emailAutomations.currentAutomation.steps.find(step => step.id === stepId);
export const selectAutomationSequences = state => state.emailAutomations.currentAutomation?.sequences || {};
export default emailAutomations;
